// Mass Balance Product Mapping — the site-wide classification that sorts products into the
// refinery's own scheme: an MB Category (Feedstock / Intermediates / Products / By-Products)
// and a Sub Category (Crude, Gasoline, Distillate, LPG, Coke, …). Fuel family lives in the
// sub-category. Persisted per-refinery via the web API (localStorage fallback).

export interface CategoryDef { id: string; label: string; }
export interface SubCatDef { id: string; label: string; category: string; }
export interface ProductMapping {
  version: number;
  categories: CategoryDef[];
  subCategories: SubCatDef[];
  assignments: Record<string, string>;   // product name -> sub-category id
  intermediatesNegative: boolean;
  updatedAt?: string;
}

// MB Categories in refinery order (feed → intermediates → products → by-products)
export const CATEGORIES: CategoryDef[] = [
  { id: 'feedstock', label: 'Feedstock' },
  { id: 'intermediates', label: 'Intermediates' },
  { id: 'products', label: 'Products' },
  { id: 'byproducts', label: 'By-Products' },
];
export const CAT_ORDER = CATEGORIES.map((c) => c.id);
export const catLabel = (id: string) => CATEGORIES.find((c) => c.id === id)?.label || id;

// Sub-categories per the refinery's scheme (id = "<category>-<sub>")
export const SUBCATS: SubCatDef[] = [
  { id: 'feedstock-crude', label: 'Crude', category: 'feedstock' },
  { id: 'feedstock-natgas', label: 'Nat Gas', category: 'feedstock' },
  { id: 'feedstock-h2', label: 'H2', category: 'feedstock' },
  { id: 'feedstock-other', label: 'Other', category: 'feedstock' },
  { id: 'intermediates-gasoline', label: 'Gasoline', category: 'intermediates' },
  { id: 'intermediates-distillate', label: 'Distillate', category: 'intermediates' },
  { id: 'intermediates-gasoil', label: 'Gas Oil', category: 'intermediates' },
  { id: 'intermediates-slop', label: 'Slop', category: 'intermediates' },
  { id: 'intermediates-lpg', label: 'LPG', category: 'intermediates' },
  { id: 'intermediates-other', label: 'Other', category: 'intermediates' },
  { id: 'products-gasoline', label: 'Gasoline', category: 'products' },
  { id: 'products-distillate', label: 'Distillate', category: 'products' },
  { id: 'products-lpg', label: 'LPG', category: 'products' },
  { id: 'products-coke', label: 'Coke', category: 'products' },
  { id: 'products-asphalt', label: 'Asphalt', category: 'products' },
  { id: 'products-decant', label: 'Decant', category: 'products' },
  { id: 'products-sulphur', label: 'Sulphur', category: 'products' },
  { id: 'products-benzene', label: 'Benzene', category: 'products' },   // aromatics (SCC4)
  { id: 'products-toluene', label: 'Toluene', category: 'products' },
  { id: 'byproducts-other', label: 'Other', category: 'byproducts' },
];
export const subCatById = (mapping: ProductMapping, id: string): SubCatDef | undefined =>
  mapping.subCategories.find((s) => s.id === id);

// A brand-new dataset starts with NO product assignments — the categories/sub-categories are the
// generic refinery scheme, and unassigned streams resolve via heuristicSubCat() (and, for OAS
// datasets, the Daily-Yields Group column). Assignments are learned/edited per dataset, never
// seeded from another site — nothing here is refinery-specific.
export const defaultMapping = (): ProductMapping => ({
  version: 2,
  categories: CATEGORIES.map((c) => ({ ...c })),
  subCategories: SUBCATS.map((s) => ({ ...s })),
  assignments: {},
  intermediatesNegative: false,
});

// Best-guess sub-category for a product not already in the mapping (new streams / other sites).
export function heuristicSubCat(product: string): string {
  const u = product.toUpperCase();
  if (/(CRUDE|DILBIT|BITUMEN|\bSYN\b|WTI|SWEET|SOUR CRUDE|DAKOTA|ALBERTA|LAKE|HARDISTY|ACCESS WESTERN|BOREALIS|COLD LAKE|\bMSW\b|BAKKEN|\bWCS\b|DIESEL BLENDSTOCK)/.test(u)) return 'feedstock-crude';
  if (/(HYDROGEN|\bH2\b)/.test(u)) return 'feedstock-h2';
  if (/(NAT GAS|NATURAL GAS|COLUMBIA GAS)/.test(u)) return 'feedstock-natgas';
  if (/\bFEED\b/.test(u)) return 'feedstock-crude';
  if (/(DRY GAS|OFF GAS|FCC COKE|FLARE)/.test(u)) return 'byproducts-other';
  if (/COKE/.test(u)) return 'products-coke';
  if (/ASPHALT/.test(u)) return 'products-asphalt';
  if (/(SULF|SULPH)/.test(u)) return 'products-sulphur';
  if (/(DECANT|DEC OIL)/.test(u)) return 'products-decant';
  if (/(SLOP)/.test(u)) return 'intermediates-slop';
  if (/(ETHYL ?BENZENE|\bEBZ\b|BENZENE)/.test(u)) return 'products-benzene';
  if (/(TOLUENE|\bTOL\b)/.test(u)) return 'products-toluene';
  if (/(PROPANE|PROPYLENE|BUTANE|\bBUTAN\b|\bLPG\b|\bUC3\b|BURNED)/.test(u)) return 'products-lpg';
  if (/(GAS ?OIL)/.test(u)) return 'intermediates-gasoil';
  if (/(DIESEL|ULSD|ULS\d?\b|\bULS\b|KERO|\bJET\b|CYCLE OIL|CAT DIST|DISTILLATE|\bDERV\b)/.test(u)) return 'products-distillate';
  if (/(GASOLINE|MOGAS|CBOB|RBOB|CARBOB|REFORMATE|ALKYLATE|\bALKY\b|NAPHTHA|\bNAP\b|\bRVP\b|SUBGRADE|BLEND ?GRADE|PREMIUM|UNLEADED|\bPREM\b|\bREG\b|\bE10\b|RAFFINATE|ISOMERATE)/.test(u)) return 'products-gasoline';
  return '';
}

const API = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';
const LS_KEY = 'mass-balance-mapping';

// Per-dataset product mapping: each customer/site (dataset) has its own categorization, kept fully
// separate — one dataset never inherits another's assignments. Pass the active dataset; an absent
// per-dataset mapping resolves to defaultMapping() (empty assignments + heuristics), not to any
// other site's mapping.
// Union in any default categories / sub-categories the stored mapping predates (e.g. Benzene/Toluene
// added later), so a heuristic that assigns to a new sub-cat id still resolves to a label instead of
// rendering "? · <id>". Never removes the user's own entries.
function withDefaults(m: ProductMapping): ProductMapping {
  const subIds = new Set((m.subCategories || []).map((s) => s.id));
  const catIds = new Set((m.categories || []).map((c) => c.id));
  return {
    ...m,
    categories: [...(m.categories || []), ...CATEGORIES.filter((c) => !catIds.has(c.id))],
    subCategories: [...(m.subCategories || []), ...SUBCATS.filter((s) => !subIds.has(s.id))],
  };
}

async function fetchMapping(q: string, lsKey: string, idToken?: string): Promise<ProductMapping | null> {
  try {
    const r = await fetch(`${API}/mass-balance-mapping${q}`, { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} });
    if (r.ok) {
      const d = await r.json();
      const m = d.mapping ?? (d.body ? JSON.parse(d.body).mapping : null);
      if (m && Array.isArray(m.categories) && Array.isArray(m.subCategories)) return withDefaults(m);
    }
  } catch { /* fall through to local */ }
  try { const ls = localStorage.getItem(lsKey); if (ls) { const m = JSON.parse(ls); if (m?.categories) return withDefaults(m); } } catch { /* ignore */ }
  return null;
}

export async function loadMapping(dataset?: string, idToken?: string): Promise<ProductMapping | null> {
  if (dataset) {
    // Only this dataset's own mapping — no cross-dataset fallback. Null => caller uses defaultMapping().
    return fetchMapping(`?dataset=${encodeURIComponent(dataset)}`, `${LS_KEY}:${dataset}`, idToken);
  }
  return fetchMapping('', LS_KEY, idToken);
}

// Always caches locally; returns whether the backend also accepted it.
export async function saveMapping(m: ProductMapping, dataset?: string, idToken?: string): Promise<{ backend: boolean }> {
  const payload = { ...m, updatedAt: new Date().toISOString() };
  const q = dataset ? `?dataset=${encodeURIComponent(dataset)}` : '';
  const lsKey = dataset ? `${LS_KEY}:${dataset}` : LS_KEY;
  try { localStorage.setItem(lsKey, JSON.stringify(payload)); } catch { /* ignore */ }
  try {
    const r = await fetch(`${API}/mass-balance-mapping${q}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
      body: JSON.stringify({ mapping: payload }),
    });
    return { backend: r.ok };
  } catch { return { backend: false }; }
}
